import { Product } from '@v2/shared/lib/types';

/**
 * The shape of the box a cover is displayed in.
 *
 * Two, deliberately. Covers vary continuously — measured across a homepage they run
 * from 0.645 to 0.893 wide-for-tall — but a box per card would defeat the point: the
 * box exists so everything below it lines up, and that only holds if every card in a
 * row shares one. Two named shapes keep the css predictable and stop a row's height
 * drifting whenever its products change.
 */
export type CoverAspect = 'tall' | 'standard';

/**
 * Written out in full so Tailwind can see them. A computed class name — even
 * `aspect-[${ratio}]` — never reaches the generated stylesheet, and the box silently
 * has no height at all.
 */
export const COVER_ASPECT: Record<CoverAspect, string> = {
  tall: 'aspect-[2/3]',
  standard: 'aspect-[3/4]',
};

/**
 * Below this, a cover is tall enough that the standard box would crop it hard.
 *
 * Books sit around 0.66 and magazines around 0.75, so it separates them with room on
 * both sides. It keys off the cover rather than the product type on purpose: a book
 * with an unusually wide cover should get the box its cover needs, not the one its
 * category implies.
 */
const TALL_BELOW = 0.71;

/**
 * One shape for a whole row, chosen from the median of what the row actually holds.
 *
 * The median rather than the mean so one odd cover cannot drag the row. Taken from
 * `featured_image`'s dimensions, which arrive in the props — measuring the loaded
 * images instead would give the server and the browser different answers, and a
 * hydration mismatch throws the server's markup away.
 */
export function coverAspectFor(products: Product[]): CoverAspect {
  const ratios = products
    .map((product) => product.featured_image)
    .filter((image) => image && image.width > 0 && image.height > 0)
    .map((image) => image.width / image.height)
    .sort((a, b) => a - b);

  if (ratios.length === 0) {
    return 'standard';
  }

  return ratios[Math.floor(ratios.length / 2)] < TALL_BELOW ? 'tall' : 'standard';
}
